import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { OverFlowIcon } from 'assests/GmsIcons';
import Button from 'Components/DesignSystem/Button/Button';
import Menu from 'Components/DesignSystem/Menu/Menu';
import { Add, Drag_n_drop, Copy, Edit, Delete } from 'Components/DesignSystem/Iconography/Icons';
import { MenuItem } from 'types/DesignSystem/Menu';
import RenameIterationDialog from './RenameIterationDialog';

type IterationSidebarProps = {
    iterations: Iteration[];
    activeIteration: string | null;
    setActiveIteration: (id: string) => void;
    addIteration: () => void;
    removeIteration: (id: string) => void;
    reorderIterations: (sourceIndex: number, destinationIndex: number) => void;
    copyIteration: (id: string) => void;
    renameIteration: (id: string, newName: number) => void;
};

/**
 * The iteration rail — DS chrome since 2026-09-08, drag logic untouched (native
 * HTML5 DnD, same handlers and payloads). The per-row menu is a portalled DS
 * `Menu` with the SAME item testids the hand-rolled one carried
 * (`copy-iteration-<id>` / `rename-iteration-<id>` / `delete-iteration-<id>`);
 * Add Iteration is a DS `Button` (was `BorderedButton` + baked-navy `AddIcon`);
 * the drag handle draws the DS `Drag` glyph in `ink-tertiary` (was `#707275`).
 */
const IterationSidebar: React.FC<IterationSidebarProps> = ({
    iterations,
    activeIteration,
    setActiveIteration,
    addIteration,
    removeIteration,
    reorderIterations,
    copyIteration,
    renameIteration
}) => {
    const [draggedItemId, setDraggedItemId] = useState<string | null>(null);
    const [dragOverItemId, setDragOverItemId] = useState<string | null>(null);
    const [openMenuId, setOpenMenuId] = useState<string | null>(null);
    const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
    const [showRenameDialog, setShowRenameDialog] = useState<boolean>(false);
    const [iterationToRename, setIterationToRename] = useState<Iteration | null>(null);

    /* The menu is `tw-fixed` and the page scrolls — close it on scroll. Outside-click
       and Escape are the DS Menu's `onDismiss`. */
    useEffect(() => {
        if (openMenuId === null) return;
        const handleScroll = () => setOpenMenuId(null);
        window.addEventListener('scroll', handleScroll, true);
        return () => window.removeEventListener('scroll', handleScroll, true);
    }, [openMenuId]);

    const handleDragStart = (e: React.DragEvent<HTMLDivElement>, id: string) => {
        setDraggedItemId(id);
        if (e.dataTransfer.setDragImage) {
            const element = document.getElementById(`iteration-${id}`);
            if (element) {
                e.dataTransfer.setDragImage(element, 20, 20);
            }
        }
        e.dataTransfer.setData('text/plain', id);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>, id: string) => {
        e.preventDefault();
        if (id !== draggedItemId) {
            setDragOverItemId(id);
        }
    };

    const handleDragEnd = () => {
        setDraggedItemId(null);
        setDragOverItemId(null);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>, targetId: string) => {
        e.preventDefault();

        if (draggedItemId && draggedItemId !== targetId) {
            const sourceIndex = iterations.findIndex(item => item.id === draggedItemId);
            const targetIndex = iterations.findIndex(item => item.id === targetId);

            if (sourceIndex !== -1 && targetIndex !== -1) {
                reorderIterations(sourceIndex, targetIndex);
            }
        }

        setDraggedItemId(null);
        setDragOverItemId(null);
    };

    const handleOverflowClick = (e: React.MouseEvent<HTMLButtonElement>, iterationId: string) => {
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        setMenuPosition({ top: rect.bottom + 2, right: window.innerWidth - rect.right });
        setOpenMenuId(openMenuId === iterationId ? null : iterationId);
    };

    const handleCopy = (iterationId: string) => {
        copyIteration(iterationId);
        setOpenMenuId(null);
    };

    const handleRenameStart = (iteration: Iteration) => {
        setIterationToRename(iteration);
        setShowRenameDialog(true);
        setOpenMenuId(null);
    };

    const handleRenameCancel = () => {
        setShowRenameDialog(false);
        setIterationToRename(null);
    };

    const handleRenameSave = (newName: number) => {
        if (iterationToRename) {
            renameIteration(iterationToRename.id, newName);
        }
        setShowRenameDialog(false);
        setIterationToRename(null);
    };

    const handleDelete = (iterationId: string) => {
        removeIteration(iterationId);
        setOpenMenuId(null);
    };

    const menuItemsFor = (iteration: Iteration): MenuItem[] => ([
        {
            id: 'copy', label: 'Copy', icon: <Copy />,
            'data-testid': `copy-iteration-${iteration.id}`,
            onClick: () => handleCopy(iteration.id),
        },
        {
            id: 'rename', label: 'Rename', icon: <Edit />,
            'data-testid': `rename-iteration-${iteration.id}`,
            onClick: () => handleRenameStart(iteration),
        },
        {
            id: 'delete', label: 'Delete', icon: <Delete />,
            'data-testid': `delete-iteration-${iteration.id}`,
            onClick: () => handleDelete(iteration.id),
        },
    ]);

    return (
        <div className="tw-w-[15%] tw-border-r tw-border-edge" data-testid="iteration-sidebar">
            {iterations.map((iteration) => (
                <div role="button"
                    id={`iteration-${iteration.id}`}
                    key={iteration.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, iteration.id)}
                    onDragOver={(e) => handleDragOver(e, iteration.id)}
                    onDragEnd={handleDragEnd}
                    onDrop={(e) => handleDrop(e, iteration.id)}
                    className={`tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-cursor-pointer tw-border-b tw-border-edge tw-relative
                       ${activeIteration === iteration.id ? 'tw-bg-surface-sunken' : ''}
                       ${draggedItemId === iteration.id ? 'tw-opacity-50' : ''}
                       ${dragOverItemId === iteration.id ? 'tw-border-t-2 tw-border-t-accent' : ''}`}
                    onClick={() => setActiveIteration(iteration.id)}
                    data-testid={`iteration-${iteration.priority}`}
                >
                    <div className="tw-flex tw-items-center tw-gap-2 tw-flex-1">
                        <div className="tw-cursor-move tw-text-ink-tertiary tw-flex">
                            <Drag_n_drop />
                        </div>
                        <span className="tw-text-ink tw-font-medium">Iteration {iteration.name}</span>
                    </div>

                    <button
                        type="button"
                        aria-label="More options"
                        aria-haspopup="menu"
                        aria-expanded={openMenuId === iteration.id}
                        className="tw-rounded-ctl tw-p-1 tw-text-ink-tertiary hover:tw-text-ink hover:tw-bg-surface-hover"
                        /* Menu dismisses on document MOUSEDOWN — stop it here so pressing
                           the open trigger toggles instead of dismiss-then-reopen. */
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => handleOverflowClick(e, iteration.id)}
                        data-testid={`overflow-menu-${iteration.priority}`}
                    >
                        <OverFlowIcon />
                    </button>

                    {openMenuId === iteration.id ? ReactDOM.createPortal(
                        <div
                            className="tw-fixed tw-z-[9999] tw-w-40"
                            style={{ top: `${menuPosition.top}px`, right: `${menuPosition.right}px` }}
                        >
                            <Menu
                                variant="compact"
                                items={menuItemsFor(iteration)}
                                data-testid={`overflow-menu-options-${iteration.id}`}
                                onDismiss={() => setOpenMenuId(null)}
                            />
                        </div>,
                        document.body
                    ) : null}
                </div>
            ))}

            <div className="tw-p-4">
                <Button
                    variant="secondary"
                    onClick={addIteration}
                    icon={<Add size={16} />}
                    iconPosition="start"
                    showIcon
                    data-testid="add-iteration-button"
                >
                    Add Iteration
                </Button>
            </div>

            <RenameIterationDialog
                isOpen={showRenameDialog}
                currentName={iterationToRename?.name || 1}
                existingNames={iterations.map(iteration => iteration.name)}
                onSave={handleRenameSave}
                onCancel={handleRenameCancel}
            />
        </div>
    );
};

export default IterationSidebar;
